<script setup>
import { computed, onMounted, watch } from "vue";
import { useStore } from "vuex";
import { activateDarkMode, deactivateDarkMode } from "@/assets/js/dark-mode";
import Toggle from "@/components/Toggle.vue";

const store = useStore();
// state
const isRTL = computed(() => store.state.isRTL);
const isNavFixed = computed(() => store.state.isNavFixed);
const sidebarType = computed(() => store.state.sidebarType);
const isPinned = computed(() => store.state.isPinned);
const darkModeState = computed(() => store.state.darkMode);

// mutations
const sidebarMinimize = (value) => {
  store.commit("navbarMinimize", value);
};

const navbarFixed = (value) => {
  store.commit("navbarFixed", value);
};

const setSidebarType = (type) => {
  store.commit("setSidebarType", type);
};

const toggleConfigurator = () => {
  store.commit("toggleConfigurator");
};

const sidebarColor = (color = "success") => {
  document.querySelector("#sidenav-main").setAttribute("data-color", color);
  localStorage.setItem("sidebarColor", color); // Guardar el color en localStorage
};

const darkMode = (value) => {
  store.commit("setDarkMode", value);
  if (value) {
    setSidebarType("bg-default");
    activateDarkMode();
  } else {
    setSidebarType("bg-white");
    deactivateDarkMode();
  }
};

// Watch para observar cambios en isPinned y actualizar las clases
watch(isPinned, (newValue) => {
  const sidenav_show = document.querySelector(".g-sidenav-show");
  if (newValue) {
    sidenav_show.classList.remove("g-sidenav-hidden");
    sidenav_show.classList.add("g-sidenav-pinned");
  } else {
    sidenav_show.classList.add("g-sidenav-hidden");
    sidenav_show.classList.remove("g-sidenav-pinned");
  }
});

// Inicializar configuración desde localStorage
const initConfig = () => {
  const savedPinnedState = localStorage.getItem("isPinned") === "true";
  const savedNavFixedState = localStorage.getItem("isNavFixed") === "true";
  const savedSidebarType = localStorage.getItem("sidebarType") || "bg-white";
  const savedShowConfig = localStorage.getItem("showConfig") === "true";
  const savedDarkMode = localStorage.getItem("darkMode") === "true";
  const savedSidebarColor = localStorage.getItem("sidebarColor") || "success";

  if (savedPinnedState !== store.state.isPinned) {
    store.commit("navbarMinimize", savedPinnedState);
  }
  if (savedNavFixedState !== store.state.isNavFixed) {
    store.commit("navbarFixed", savedNavFixedState);
  }
  if (savedSidebarType !== store.state.sidebarType) {
    store.commit("setSidebarType", savedSidebarType);
  }
  if (savedShowConfig !== store.state.showConfig) {
    store.commit("toggleConfigurator");
  }
  if (savedDarkMode !== store.state.darkMode) {
    store.commit("setDarkMode", savedDarkMode);
    if (savedDarkMode) {
      activateDarkMode();
    } else {
      deactivateDarkMode();
    }
  }
  document.querySelector("#sidenav-main").setAttribute("data-color", savedSidebarColor);

  // Aplicar clases CSS adecuadas al elemento de la barra lateral
  const sidenav_show = document.querySelector(".g-sidenav-show");
  if (savedPinnedState) {
    sidenav_show.classList.remove("g-sidenav-hidden");
    sidenav_show.classList.add("g-sidenav-pinned");
  } else {
    sidenav_show.classList.add("g-sidenav-hidden");
    sidenav_show.classList.remove("g-sidenav-pinned");
  }
};

// Llamar a initConfig cuando el componente se monte
onMounted(() => {
  initConfig();
});
</script>

<template>
  <div class="fixed-plugin">
    <a class="px-3 py-2 fixed-plugin-button text-dark position-fixed" @click="toggleConfigurator">
      <i class="fa-solid fa-gear"></i>
    </a>
    <div class="shadow-lg card">
      <div class="pt-3 pb-0 bg-transparent card-header">
        <div class="float-start">
          <h5 class="mt-3 mb-0">Configuración</h5>
          <p>See our dashboard options.</p>
        </div>
        <div class="mt-4 float-end" @click="toggleConfigurator">
          <button class="p-0 btn btn-link text-dark fixed-plugin-close-button">
            <i class="fa fa-close"></i>
          </button>
        </div>
      </div>
      <hr class="my-1 horizontal dark" />
      <div class="pt-0 card-body pt-sm-3">
        <!-- Sidebar Colors -->
        <div>
          <h6 class="mb-0">Sidebar Colors</h6>
        </div>
        <div class="switch-trigger background-color">
          <div class="my-2 badge-colors" :class="isRTL ? 'text-end' : 'text-start'">
            <span class="badge filter bg-gradient-primary" data-color="primary" @click="sidebarColor('primary')"></span>
            <span class="badge filter bg-gradient-dark" data-color="dark" @click="sidebarColor('dark')"></span>
            <span class="badge filter bg-gradient-info" data-color="info" @click="sidebarColor('info')"></span>
            <span class="badge filter bg-gradient-success" data-color="success" @click="sidebarColor('success')"></span>
            <span class="badge filter bg-gradient-warning" data-color="warning" @click="sidebarColor('warning')"></span>
            <span class="badge filter bg-gradient-danger" data-color="danger" @click="sidebarColor('danger')"></span>
          </div>
        </div>
        <!-- Sidenav Type -->
        <div class="mt-3">
          <h6 class="mb-0">Sidenav Type</h6>
          <p class="text-sm">Choose between 2 different sidenav types.</p>
        </div>
        <div class="d-flex">
          <button
id="btn-white" class="btn bg-gradient-success w-100 px-3 mb-2 me-2"
            :class="sidebarType === 'bg-white' ? ' active' : ''" @click="setSidebarType('bg-white')">
            White
          </button>
          <button
id="btn-dark" class="btn bg-gradient-success w-100 px-3 mb-2"
            :class="sidebarType === 'bg-default' ? 'active' : ''" @click="setSidebarType('bg-default')">
            Dark
          </button>
        </div>
        <p class="mt-2 text-sm d-xl-none d-block">
          You can change the sidenav type just on desktop view.
        </p>
        <!-- Navbar Fixed -->
        <Toggle id="isNavFixed" :model-value="isNavFixed" label="Navbar Fixed" @update:model-value="navbarFixed" />

        <hr class="my-3 horizontal dark" />
        <!-- Sidebar Mini -->
        <Toggle id="isPinned" :model-value="isPinned" label="Sidenav Mini" @update:model-value="sidebarMinimize" />

        <hr class="horizontal dark my-3" />
        <!-- Dark Mode -->
        <Toggle id="darkMode" :model-value="darkModeState" label="Light / Dark" @update:model-value="darkMode" />

        <hr class="horizontal dark my-sm-4" />
        <a class="btn btn-success w-100" href="https://www.creative-tim.com/product/vue-argon-dashboard-pro">Buy now</a>
        <a class="btn bg-gradient-dark w-100" href="https://www.creative-tim.com/product/vue-argon-dashboard">Free
          demo</a>
        <a
class="btn btn-outline-dark w-100"
          href="https://www.creative-tim.com/learning-lab/vue/overview/argon-dashboard/">View documentation</a>
        <div class="text-center w-100">
          <a
class="github-button" href="https://github.com/creativetimofficial/ct-vue-argon-dashboard-pro"
            data-icon="octicon-star" data-size="large" data-show-count="true"
            aria-label="Star creativetimofficial/vue-argon-dashboard-pro on GitHub">Star</a>
          <h6 class="mt-3">Thank you for sharing!</h6>
          <a
href="https://twitter.com/intent/tweet?text=Check%20Vue%20Argon%20Dashboard%20PRO%20made%20by%20%40CreativeTim%20%23webdesign%20%23dashboard%20%23bootstrap5&url=https%3A%2F%2Fwww.creative-tim.com%2Fproduct%2Fvue-argon-dashboard-pro"
            class="mb-0 btn btn-dark me-2" target="_blank">
            <i class="fab fa-twitter me-1" aria-hidden="true"></i> Tweet
          </a>
          <a
href="https://www.facebook.com/sharer/sharer.php?u=https://www.creative-tim.com/product/vue-argon-dashboard-pro"
            class="mb-0 btn btn-dark me-2" target="_blank">
            <i class="fab fa-facebook-square me-1" aria-hidden="true"></i> Share
          </a>
        </div>
      </div>
    </div>
  </div>
</template>
